import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { CalAngularService } from '@cvx/cal-angular';
import { environment } from '../../environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    constructor(
        private router: Router,
        private calService: CalAngularService
    ) { }

    /**
     * Safely check if the URL hostname matches expected patterns
     * Using URL parsing instead of string includes() to prevent bypass attacks
     */
    private isAllowedApiUrl(url: string): { isGraphApi: boolean; isBackendApi: boolean } {
        try {
            const parsedUrl = new URL(url, window.location.origin);
            const hostname = parsedUrl.hostname.toLowerCase();
            
            const isGraphApi = hostname === 'graph.microsoft.com';
            const isBackendApi = hostname === 'localhost' || 
                                 hostname.endsWith('.llfem.com') || 
                                 hostname === 'llfem.com' ||
                                 hostname.includes('llfem');
            
            return { isGraphApi, isBackendApi };
        } catch {
            // If URL parsing fails, don't attach tokens
            return { isGraphApi: false, isBackendApi: false };
        }
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Check if this is a Graph API or your backend API call that needs a token
        const { isGraphApi, isBackendApi } = this.isAllowedApiUrl(request.url);

        if (isGraphApi || isBackendApi) {
            return from(this.getAccessToken(isGraphApi)).pipe(
                switchMap(token => {
                    if (token) {
                        request = request.clone({
                            headers: request.headers
                                .set('Accept', 'application/json')
                                .set('Authorization', `Bearer ${token}`)
                        });
                    } else {
                        request = request.clone({ headers: request.headers.set('Accept', 'application/json') });
                    }
                    return next.handle(request).pipe(
                        map((event: HttpEvent<any>) => {
                            if (event instanceof HttpResponse) {}
                            return event;
                        }),
                        catchError((err) => this.handleError(err))
                    );
                }),
                catchError((err) => {
                    console.error('Error getting access token:', err);
                    // Continue without token if token acquisition fails
                    request = request.clone({ headers: request.headers.set('Accept', 'application/json') });
                    return next.handle(request).pipe(
                        catchError((err) => this.handleError(err))
                    );
                })
            );
        }

        request = request.clone({ headers: request.headers.set('Accept', 'application/json') });
        return next.handle(request).pipe(
          map((event: HttpEvent<any>) => {
            if (event instanceof HttpResponse) {}
            return event;
        }),
          catchError((err) => this.handleError(err))
        );
      }

    private async getAccessToken(isGraphApi: boolean): Promise<string | null> {
        try {
            const principal = this.calService.cvxClaimsPrincipal;
            if (!principal) {
                console.warn('No claims principal available');
                return null;
            }

            // For Graph API, use Graph scopes; for backend API, use API scopes
            const scopes = isGraphApi 
                ? ['User.Read'] 
                //: ['https://llfem-test.azure.chevron.com/user_impersonation'];
                : [environment.azureAd.scope.userImpersonation];

            const result = await this.calService.getAADToken(scopes).toPromise();
            console.log('Token acquired for:', isGraphApi ? 'Graph API' : 'Backend API');
            
            if (!result) {
                return null;
            }
            // Result can be string or AuthenticationResult
            if (typeof result === 'string') {
                return result;
            }
            return result.accessToken || null;
        } catch (error) {
            console.error('Failed to acquire token:', error);
            return null;
        }
    }
    
      handleError(err: HttpErrorResponse): Observable<HttpEvent<any>> {
        if (err.error instanceof Error) {
          console.log('An error occurred:', err.error.message);
        } else {
          console.log(`Backend returned code ${err.status}, body was: ${err.error}`);
          if (err.status === 401) {
            this.router.navigate(['/']);
          }
          else if (err.status === 0) {
            err.error.message = "Unable to connect to the server.";
          }
        }
        return throwError(() => err);
    
      }
}
